import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import DataTable, { type Column } from "@/components/DataTable";
import type { Portal, SsoProvider, SsoProviderType } from "@/types";
import { useSsoProviders } from "../hooks/useSsoProviders";

const providerTypeLabels: Record<SsoProviderType, string> = {
  google: "Google",
  microsoft: "Microsoft",
  oidc: "OIDC (Okta, Auth0, etc.)",
  saml: "SAML 2.0",
};

interface SsoTabProps {
  portal: Portal;
}

const SsoTab: React.FC<SsoTabProps> = ({ portal }) => {
  const navigate = useNavigate();
  const {
    providers,
    isLoading,
    deleteProvider,
    isDeleting,
  } = useSsoProviders(portal.id);

  const handleAdd = () => {
    navigate(`/dashboard/portals/${portal.id}/sso/add`);
  };

  const handleEdit = (provider: SsoProvider) => {
    navigate(`/dashboard/portals/${portal.id}/sso/${provider.id}/edit`);
  };

  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const handleConfirmDelete = (id: string) => {
    deleteProvider(id);
    setConfirmDeleteId(null);
  };

  const columns: Column<SsoProvider>[] = [
    {
      id: "display_name",
      label: "Name",
      render: (row) => (
        <Stack>
          <Typography variant="body2" fontWeight={600}>
            {row.display_name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {row.provider_key}
          </Typography>
        </Stack>
      ),
    },
    {
      id: "provider_type",
      label: "Type",
      render: (row) => (
        <Chip
          label={providerTypeLabels[row.provider_type] ?? row.provider_type}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      id: "target_audience",
      label: "Audience",
      render: (row) => (
        <Chip
          label={row.target_audience === "provider" ? "Provider" : "Client"}
          color={row.target_audience === "provider" ? "info" : "default"}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      id: "status",
      label: "Status",
      render: (row) => (
        <Chip
          label={row.is_enabled ? "Enabled" : "Disabled"}
          color={row.is_enabled ? "success" : "default"}
          size="small"
        />
      ),
    },
    {
      id: "actions",
      label: "Actions",
      align: "right",
      render: (row) => {
        if (confirmDeleteId === row.id) {
          return (
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.5}
              justifyContent="flex-end"
            >
              <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                Delete?
              </Typography>
              <Tooltip title="Confirm delete">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleConfirmDelete(row.id)}
                  disabled={isDeleting}
                >
                  <CheckIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Cancel">
                <IconButton
                  size="small"
                  onClick={() => setConfirmDeleteId(null)}
                  disabled={isDeleting}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          );
        }
        return (
          <>
            <Tooltip title="Delete provider">
              <IconButton
                size="small"
                color="error"
                onClick={() => setConfirmDeleteId(row.id)}
                disabled={isDeleting}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit provider">
              <IconButton size="small" onClick={() => handleEdit(row)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        );
      },
    },
  ];

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1 }}
      >
        <Box>
          <Typography variant="body1" fontWeight={600}>
            SSO Providers
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure single sign-on providers for chat user authentication.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
          Add Provider
        </Button>
      </Stack>

      <DataTable
        columns={columns}
        data={providers}
        keyExtractor={(row) => row.id}
        loading={isLoading}
        emptyMessage="No SSO providers configured"
        emptyDescription="Add an SSO provider to enable single sign-on for chat users."
        sx={{ mt: 2 }}
      />
    </Box>
  );
};

export default SsoTab;
